import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { VAutocompleteComponent } from "@app/directives/_forms/v-autocomplete/v-autocomplete.component";
import { UserType } from "@app/enum/UserType";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { IUser } from "@app/interfaces/IUser";
import { IVehicle } from "@app/interfaces/IVehicle";
import { EngineerJobItemType } from "@app/main/modules/foundation/foundation.enums";
import { IEngineer, IEngineerCommission, IEngineerJobItem } from "@app/main/modules/foundation/foundation.interfaces";
import { AccountService } from "@app/main/modules/foundation/_services/account.service";
import { EngineerJobItemService } from "@app/main/modules/foundation/_services/engineer-job-item.service";
import { EngineerJobService } from "@app/main/modules/foundation/_services/engineer-job.service";
import { EngineerService } from "@app/main/modules/foundation/_services/engineer.service";
import { FoundationVehiclesService } from "@app/main/modules/foundation/_services/vehicle.service";
import { IAccount } from "@app/services/account.service";
import { CurrentUserService } from "@app/services/currentUser.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { delay, take } from "rxjs/operators";
import './new-commission-detail.component.scss';

@UntilDestroy()
@Component({
    selector: 'new-commission-detail',
    template: require('./new-commission-detail.component.html')
})
export class NewCommissionDetailComponent implements OnInit, OnChanges {
    @Input() commission: IEngineerCommission;
    @Input() jobItemId: number;

    @Output() commissionChanged = new EventEmitter<IEngineerCommission>();

    @ViewChild('vehicleAutocomplete') vehicleAutocomplete: VAutocompleteComponent<IVehicle>;
    //--
    loading: boolean;
    vehiclesLoading: boolean;

    vehicleConfirmed = false;
    editVehicleDetails: boolean;

    engineerJobItemType = EngineerJobItemType;
    visitReasons = Object.keys(EngineerJobItemType).filter(s => !isNaN(Number(s))).map(s => Number(s));

    VehicleTypes = VehicleType;
    vehicleTypes = Object.keys(VehicleType).filter(s => !isNaN(Number(s))).map(s => Number(s));


    accounts: IAccount[];
    engineers: IUser[];
    availableEngineers: IUser[];
    vehicles: IVehicle[];
    allVehicles: IVehicle[];
    currentUser: IUser;
    jobItem: IEngineerJobItem;
    userIsEngineer = false;

    newVehicle: IVehicle;
    newVehicleReg: string;

    selectedVisitReason: EngineerJobItemType;
    selectedEngineer: IUser;
    selectedEngineerLegacy: IEngineer;
    selectedAccount: IAccount;
    selectedVehicle: IVehicle;
    selectedVehicleType: VehicleType;

    commissionForm: FormGroup;

    showAddVehicleForm: boolean;
    showAddVehicleButton: boolean;

    constructor(
        private readonly accountsService: AccountService,
        private readonly currentUserService: CurrentUserService,
        private readonly engineerService: EngineerService,
        private readonly formBuilder: FormBuilder,
        private readonly jobItemService: EngineerJobItemService,
        private readonly jobService: EngineerJobService,
        private readonly vehicleService: FoundationVehiclesService) { }

    ngOnInit(): void {
        this.loadData();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes['commission'] && this.commission) {
            this.createForm();
        }

        if (!!this.jobItem && !this.commission.JobNumber) {
            this.commission.JobNumber = this.jobItem.JobId;
        }

        if (!!this.jobItemId && !this.selectedVehicle) {
            this.showAddVehicleButton = true;
        }
    }


    // Autocomplete select actions
    onVisitReasonSelected(reason: EngineerJobItemType): void {
        if (!this.commissionForm) {
            return;
        }

        this.selectedVisitReason = reason;
        this.commissionForm.get('visitReasonId').setValue(reason);

    }

    onAccountSelected(account: IAccount): void {
        if (!account) {
            return;
        }
        this.commission.account = account;
        this.commissionForm.get('accountId').setValue(account.Id);
        this.selectedAccount = account;
        this.getVehicles();
    }

    onEngineerSelected(engineer: IUser): void {
        if (!this.commissionForm) {
            return;
        }

        this.selectedEngineer = engineer;
        this.commission.engineerUser = engineer;
        this.commissionForm.get('userId').setValue(engineer.Id);
    }

    onLegacyEngineerSelected(engineer: IEngineer): void {
        if (!this.commissionForm) {
            return;
        }

        this.selectedEngineerLegacy = engineer;
        this.commission.engineerName = engineer.Name;
        this.commissionForm.get('engineerId').setValue(engineer.Id);
    }

    onVehicleSelected(vehicle: IVehicle): void {
        if (!vehicle) {
            return;
        }
        this.clearVehicle();

        this.selectedVehicle = vehicle;
        this.commission.vehicle = vehicle;
        this.selectedVehicleType = vehicle.VehicleType ?? VehicleType.Unknown;
        this.vehicleConfirmed = false;
        this.commission.pendingCommissionChanges = [];
        this.commissionForm.get('vehicleConfirmed').setValue(this.vehicleConfirmed);
        this.commissionForm.get('vehicleId').setValue(vehicle.Id);
        this.commissionForm.get('vehicleMake').setValue(vehicle.Make);
        this.commissionForm.get('vehicleModel').setValue(vehicle.Model);
        this.commissionForm.get('vehicleType').setValue(this.selectedVehicleType);
        if (!VehicleType[this.selectedVehicleType] || this.selectedVehicleType === VehicleType.None || this.selectedVehicleType === VehicleType.Unknown) {
            this.toggleEditVehicleDetails(true);
        }
    }


    //Autocomplete search predicates
    accountSearchPredicate(account: IAccount, text: string): boolean {
        return account.CompanyName.toLowerCase().includes(text.toLowerCase());
    }

    engineerSearchPredicate(engineer: IUser, text: string): boolean {
        return engineer.Description?.toLowerCase().includes(text.toLowerCase());
    }

    engineerLegacySearchPredicate(engineer: IEngineer, text: string): boolean {
        return engineer.Name?.toLowerCase().includes(text.toLowerCase());
    }

    vehicleSearchPredicate(vehicle: IVehicle, text: string): boolean {
        return vehicle.ShortReg.toLowerCase().includes(text.toLowerCase()) || vehicle.ShortReg.toLowerCase().includes(text.toLowerCase());
    }

    // Value change events
    onRegChanged(value: string): void {
        this.newVehicle.Reg = this.newVehicle.ShortReg = value;

    }

    onMakeChanged(value: string): void {
        if (this.newVehicle) {
            this.newVehicle.Make = value;
            return;
        }
        this.commissionForm.get('vehicleMake').setValue(value);
    }

    onModelChanged(value: string): void {
        if (this.newVehicle) {
            this.newVehicle.Model = value;
            return;
        }
        this.commissionForm.get('vehicleModel').setValue(value);
    }

    onVehicleTypeChanged(value: VehicleType): void {
        if (this.newVehicle) {
            this.newVehicle.VehicleType = value;
            return;
        }
        this.commissionForm.get('vehicleType').setValue(value);
        this.selectedVehicleType = value;
    }

    // Toggles
    toggleEditVehicleDetails(toggle: boolean): void {
        this.editVehicleDetails = toggle;
    }

    toggleConfirmVehicle(): void {
        this.vehicleConfirmed = !this.vehicleConfirmed;
        this.commissionForm.get('vehicleConfirmed').setValue(this.vehicleConfirmed);
    }

    toggleShowAddVehicleForm(): void {
        this.showAddVehicleForm = true;
        this.newVehicle = {
            Id: 0,
            Reg: null,
            ShortReg: null,
            Make: null,
            Model: null,
            VehicleType: VehicleType.Unknown
        }

        this.vehicleAutocomplete?.clearInput();
    }

    onNoVehiclesFound(searchTerm: string): void {
        this.showAddVehicleButton = true;
        this.vehicleConfirmed = false;
        this.commissionForm.get('vehicleConfirmed').setValue(this.vehicleConfirmed);
        this.newVehicleReg = searchTerm;
    }


    // Autocomplete clear actions
    clearAccount(): void {
        this.commission.account = null;
        this.selectedAccount = null;
        this.commissionForm.get('accountId').setValue(null);
        this.clearVehicle();
    }

    clearVehicle(): void {
        this.commission.VehicleId = null;
        this.commission.vehicle = null;
        this.selectedVehicle = null;
        this.vehicleConfirmed = false;
        this.showAddVehicleButton = false;
        this.commissionForm.get('vehicleConfirmed').setValue(this.vehicleConfirmed);
        this.commissionForm.get('vehicleMake').setValue(null);
        this.commissionForm.get('vehicleModel').setValue(null);
        this.commissionForm.get('vehicleType').setValue(null);
    }

    clearEngineer(): void {
        this.selectedEngineer = null;
        this.commissionForm.get('userId').setValue(null);
    }

    clearEngineerLegacy(): void {
        this.selectedEngineerLegacy = null;
        this.commission.engineerName = "";
        this.commissionForm.get('engineerId').setValue(null);
    }

    //

    onNewVehicleAdded(vehicle: IVehicle): void {
        this.vehicles.push(vehicle);
        this.onVehicleSelected(vehicle);
        this.showAddVehicleForm = false;
        this.newVehicle = null;
        
        let observable: Observable<IEngineerJobItem> = of(null);

        if (!!this.jobItem) {
            this.jobItem.VehicleId = vehicle.Id;
            observable = this.jobItemService.createUpdateEngineerJobItem(this.jobItem);
        }

        observable.pipe(take(1), delay(2000)).subscribe(jobItem => {
                this.vehicleConfirmed = true;
                this.commissionForm.get('vehicleConfirmed').setValue(this.vehicleConfirmed);            
        });
    }

    // Getters
    get formIsValid(): boolean {
        return this.commissionForm?.valid;
    }

    get commissionUserId(): number {
        return this.commissionForm?.get("userId").value;
    }

    get commissionAccountId(): number {
        return this.commissionForm?.get("accountId").value;
    }

    get commissionVehicleId(): number {
        return this.commissionForm?.get("vehicleId").value;
    }

    get canAddNewVehicle(): boolean {
        return (this.commission?.VisitReasonId === EngineerJobItemType.Install
            && (this.showAddVehicleButton)
            && !this.commission.vehicle)
            || (this.userIsEngineer && !this.commission.vehicle);
    }

    //--

    private loadData(): void {
        this.loading = true;

        this.getUsers().then(() => {
            combineLatest([
                this.accountsService.getCustomerAccounts(),
                this.engineerService.getEngineers(),
                this.jobItemId ? this.jobItemService.getEngineerJobItems({ jobItemId: this.jobItemId }) : of([])
            ])
                .pipe(untilDestroyed(this))
                .subscribe(([accounts, engineers, jobItems]) => {
                    this.accounts = accounts;
                    this.engineers = engineers;
                    this.availableEngineers = engineers.filter(e => e.IsActive)
                    this.jobItem = jobItems[0];
                    this.mapDataToCommission();
                    this.loading = false;
                })
        })


    }

    private getUsers(): Promise<any> {
        return Promise.all([
            this.currentUserService.getCurrentUser() as Promise<IUser>
        ]).then(([currentUser]) => {
            this.currentUser = currentUser;
        })
    }

    private getVehicles(): void {
        this.vehiclesLoading = true;
        this.vehicleService.getVehiclesForAccount(this.commission.AccountId)
            .pipe(untilDestroyed(this))
            .subscribe(vehicles => {
                this.vehicles = vehicles;
                if (this.jobItem) {
                    this.onVehicleSelected(vehicles.find(veh => veh.Id === this.jobItem.VehicleId));
                }
                this.vehiclesLoading = false;
            })
    }

    private createForm(): void {
        if (!this.commission || this.commissionForm) {
            return;
        }

        this.commissionForm = this.formBuilder.group({
            visitReasonId: [this.commission.VisitReasonId, [Validators.required]],
            accountId: [this.commission.AccountId, [Validators.required]],
            userId: [this.commission.EngineerUserId, [Validators.required]],
            vehicleId: [this.commission.VehicleId, [Validators.required]],
            vehicleMake: [this.commission.vehicle?.Make],
            vehicleModel: [this.commission.vehicle?.Model],
            vehicleType: [this.commission.vehicle?.VehicleType, [Validators.required]],
            vehicleConfirmed: [this.vehicleConfirmed, [Validators.required, this.requiredTrue]]
        })

        this.commissionForm.statusChanges.pipe(untilDestroyed(this)).subscribe((status: string) => {
            this.commission.VisitReasonId = +this.commissionForm.get("visitReasonId").value;
            this.commission.AccountId = +this.commissionForm.get("accountId").value;
            this.commission.EngineerUserId = +this.commissionForm.get("userId").value;
            this.commission.VehicleId = +this.commissionForm.get("vehicleId").value;

            if (this.commission.vehicle) {
                this.commission.vehicle.Make = this.commissionForm.get("vehicleMake").value || this.commission.vehicle.Make;
                this.commission.vehicle.Model = this.commissionForm.get("vehicleModel").value || this.commission.vehicle.Model;
                this.commission.vehicle.VehicleType = +this.commissionForm.get("vehicleType").value;
            }

            this.commissionChanged.emit(this.commission);
        })
    }

    private mapDataToCommission(): void {
        if (this.currentUser.Type === UserType.Engineer) {
            this.userIsEngineer = true;
            this.onEngineerSelected(this.currentUser);
        }
        if (this.commission.AccountId) {
            const account = this.accounts.find(account => account.Id === this.commission.AccountId);
            this.onAccountSelected(account);
        }

        this.onVisitReasonSelected(this.commission.VisitReasonId);
        this.mapJobItemToCommission();
    }

    private mapJobItemToCommission(): void {
        if (!this.jobItem) {
            return;
        }

        this.jobService.getEngineerJob(this.jobItem.JobId).pipe(take(1)).subscribe(job => {
            this.onVisitReasonSelected(this.jobItem.Type);
            this.onAccountSelected(this.accounts.find(acc => acc.Id === job.AccountId));
            this.onVehicleSelected(this.vehicles?.find(veh => veh.Id === this.jobItem.VehicleId));
        })
    }

    requiredTrue = (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return {
                "required": { valid: false }
            }
        }
    }
}
